import React from "react";
import { Link } from "react-router-dom";
import CTAButton from "../CTAButton/CTAButton";
import "../MailchimpAlternative/MailchimpAlternative.css";
import "./SeoLandingPage.css";

const PAGES = {
  software: {
    eyebrow: "Email marketing software",
    h1: "Email Marketing Software for Growing Businesses",
    intro: "Mailsnappy helps teams create polished campaigns, manage subscribers, automate follow-ups, and understand campaign performance from one simple email marketing workspace.",
    primaryLink: "/templates",
    primaryText: "Explore email templates",
    secondaryLink: "/automation",
    secondaryText: "See email automation",
    sections: [
      ["Create campaigns faster", "Start with responsive email templates, customize layouts, and build messages that fit newsletters, promotions, and customer updates."],
      ["Manage subscribers clearly", "Use audience tools, signup forms, tags, and segmentation to keep contacts organized for more relevant campaigns."],
      ["Improve every send", "Review campaign analytics so your next email is informed by opens, clicks, engagement, and subscriber behavior."],
    ],
  },
  platform: {
    eyebrow: "Email marketing platform",
    h1: "Email Marketing Platform for Campaigns and Automation",
    intro: "Mailsnappy brings templates, AI-assisted design, audience growth, automation, segmentation, and analytics together so teams can run email marketing without scattered tools.",
    primaryLink: "/features",
    primaryText: "View platform features",
    secondaryLink: "/pricing",
    secondaryText: "View pricing",
    sections: [
      ["One place for email work", "Plan campaigns, build templates, organize subscribers, and measure results from connected marketing pages."],
      ["Automation built into the workflow", "Create welcome sequences and follow-ups that help subscribers receive the right message at the right moment."],
      ["Useful for growing teams", "Mailsnappy is designed for businesses that need practical email marketing tools without unnecessary complexity."],
    ],
  },
  automation: {
    eyebrow: "Email automation software",
    h1: "Email Automation Software for Customer Journeys",
    intro: "Use Mailsnappy to build automated email sequences for welcomes, follow-ups, nurturing, and lifecycle messages that keep subscribers engaged.",
    primaryLink: "/automation-builder",
    primaryText: "Open automation builder",
    secondaryLink: "/automation",
    secondaryText: "Explore automation",
    sections: [
      ["Welcome new subscribers", "Send timely welcome emails that introduce your brand and guide new contacts toward the next useful step."],
      ["Nurture leads with less manual work", "Trigger follow-up campaigns and customer journeys so repeated communication does not rely on manual sends."],
      ["Measure automated campaigns", "Track performance and use campaign data to refine your automated email journeys over time."],
    ],
  },
  newsletter: {
    eyebrow: "Newsletter software",
    h1: "Newsletter Software for Branded Email Campaigns",
    intro: "Design and send newsletters that look professional in every inbox using Mailsnappy templates, audience tools, and campaign reporting.",
    primaryLink: "/templates",
    primaryText: "Browse newsletter templates",
    secondaryLink: "/email-builder",
    secondaryText: "Use the email builder",
    sections: [
      ["Start with responsive layouts", "Choose newsletter-ready email templates and customize them with your own copy, links, images, and brand details."],
      ["Send to organized audiences", "Use subscriber groups and segmentation to share updates with the contacts most likely to value them."],
      ["Learn from each newsletter", "Campaign analytics help you understand how subscribers respond so future newsletters can improve."],
    ],
  },
  campaign: {
    eyebrow: "Email campaign software",
    h1: "Email Campaign Software for Better Marketing",
    intro: "Mailsnappy gives you the tools to plan, build, send, automate, and measure email campaigns for product launches, promotions, newsletters, and customer updates.",
    primaryLink: "/features",
    primaryText: "Explore campaign tools",
    secondaryLink: "/analytics",
    secondaryText: "See campaign analytics",
    sections: [
      ["Build campaign-ready emails", "Use templates and the email builder to create professional messages without starting from a blank screen."],
      ["Target the right subscribers", "Combine audience tools and segmentation to match each campaign with a relevant group of contacts."],
      ["Track campaign performance", "Use opens, clicks, and engagement reporting to understand what worked and where to improve."],
    ],
  },
  templateBuilder: {
    eyebrow: "Email template builder",
    h1: "Email Template Builder for Responsive Campaigns",
    intro: "Create responsive email templates faster with Mailsnappy's ready-made layouts, visual previews, and AI-assisted template design tools.",
    primaryLink: "/ai-template-designer",
    primaryText: "Try AI template designer",
    secondaryLink: "/templates",
    secondaryText: "Browse templates",
    sections: [
      ["Design without coding", "Customize reusable layouts for newsletters, promotions, lifecycle emails, and announcements without editing HTML by hand."],
      ["Preview campaign layouts", "Check how templates look before using them in campaigns so your emails feel polished and on brand."],
      ["Reuse what works", "Save time by starting from campaign-ready designs and adapting them for future sends."],
    ],
  },
};

const relatedPages = {
  software: [
    ["email marketing platform", "/email-marketing-platform"],
    ["email automation software", "/email-automation-software"],
    ["email campaign software", "/email-campaign-software"],
  ],
  platform: [
    ["email marketing software", "/email-marketing-software"],
    ["email templates", "/templates"],
    ["email automation features", "/automation"],
  ],
  automation: [
    ["audience segmentation", "/segmentation"],
    ["email templates", "/templates"],
    ["email campaign software", "/email-campaign-software"],
  ],
  newsletter: [
    ["email template builder", "/email-template-builder"],
    ["responsive email templates", "/templates"],
    ["email marketing software", "/email-marketing-software"],
  ],
  campaign: [
    ["email marketing features", "/features"],
    ["campaign analytics", "/analytics"],
    ["email automation software", "/email-automation-software"],
  ],
  templateBuilder: [
    ["email templates", "/templates"],
    ["AI email template designer", "/ai-template-designer"],
    ["email campaign software", "/email-campaign-software"],
  ],
};

export default function SeoLandingPage({ type }) {
  const page = PAGES[type] || PAGES.software;
  const related = relatedPages[type] || relatedPages.software;

  return (
    <main className="mailchimp-page seo-landing-page">
      <section className="mailchimp-hero seo-landing-hero">
        <div className="mailchimp-hero-copy">
          <span className="mailchimp-eyebrow">{page.eyebrow}</span>
          <h1>{page.h1}</h1>
          <p>{page.intro}</p>
          <div className="mailchimp-actions">
            <Link to={page.primaryLink}>{page.primaryText}</Link>
            <Link className="secondary" to={page.secondaryLink}>{page.secondaryText}</Link>
          </div>
        </div>
        <div className="seo-landing-panel" aria-label={`${page.eyebrow} workflow`}>
          <div className="seo-landing-window">
            <span />
            <span />
            <span />
          </div>
          <div className="seo-landing-lines">
            <i />
            <i />
            <i />
          </div>
          <div className="seo-landing-mini-grid">
            <b>Templates</b>
            <b>Audience</b>
            <b>Automation</b>
            <b>Reports</b>
          </div>
        </div>
      </section>

      <section className="mailchimp-proof">
        {page.sections.map(([title, text]) => (
          <article key={title}>
            <h2>{title}</h2>
            <p>{text}</p>
          </article>
        ))}
      </section>

      <section className="mailchimp-better">
        <div>
          <span>Built for useful email marketing</span>
          <h2>Connect campaigns, subscribers, and results</h2>
          <p>
            Keep moving through the normal email workflow: choose a template, tailor it for your audience, automate the right moments, and review performance after sending.
          </p>
          <div className="related-page-links" aria-label="Related Mailsnappy pages">
            {related.map(([label, href]) => <Link key={href} to={href}>{label}</Link>)}
          </div>
        </div>
        <CTAButton className="primary-btn">Get started</CTAButton>
      </section>
    </main>
  );
}
